import { useEffect, useState, type RefObject } from 'react';

/** Reserves the actual captions and status messages around a contained video frame. */
export function usePreviewStageSize(container: RefObject<HTMLDivElement | null>, revision: string) {
  const [size, setSize] = useState({ width: 320, height: 560, fitWidth: false });
  useEffect(() => {
    const element = container.current;
    if (!element) return;
    const details = [...element.children].filter(child => !child.classList.contains('composition-preview-canvas'));
    const measure = () => {
      const reserved = details.reduce((height, child) => {
        const style = getComputedStyle(child);
        if (style.display === 'none') return height;
        return height + child.getBoundingClientRect().height + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
      }, 0);
      const style = getComputedStyle(element);
      const padding = (name: string) => parseFloat(style.getPropertyValue(name)) || 0;
      const width = Math.max(0, element.clientWidth - padding('padding-left') - padding('padding-right'));
      const height = Math.max(0, element.clientHeight - reserved - padding('padding-top') - padding('padding-bottom'));
      const fitWidth = getComputedStyle(element).getPropertyValue('--preview-fit').trim() === 'width';
      setSize(current => current.width === width && current.height === height && current.fitWidth === fitWidth
        ? current : { width, height, fitWidth });
    };
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    for (const child of details) observer.observe(child);
    measure();
    return () => observer.disconnect();
  }, [container, revision]);
  return size;
}
